import { BRAND, getCountry } from './brand';
import { getSearchSessionId } from './searchSession';
import type { SearchCard, SiteSearchResponse } from '../types';

export type { SearchCard, SiteSearchResponse };

const API_BASE = 'https://app.trip-planner.com.au/api';
const MIN_QUERY_LENGTH = 2;

// 8s mirrors fetchUnifiedSearchResults's timeout in searchEntities.tsx: the
// memory watchdog occasionally restarts flask-prod mid-request, and browser
// fetch has no default timeout, so an accepted-but-never-answered connection
// would otherwise hang this promise (and the caller's `await`) forever.
const REQUEST_TIMEOUT_MS = 8000;

export const EMPTY_SEARCH_RESPONSE: SiteSearchResponse = {
  query: '',
  total: 0,
  counts: {},
  results: [],
  smartCard: null,
  degraded: false,
};

interface FetchSearchOptions {
  mode?: 'suggest' | 'full';
  types?: string[];
  page?: number;
  limit?: number;
  /** Which search box this came from. Drives the Insights surface filter. */
  surface?: string;
}

/**
 * Query the unified search API.
 *
 * Never throws: search is chrome on every page, so a failure — network
 * error, non-2xx, malformed JSON, or a request that times out — degrades to
 * an empty response rather than an error boundary or a white screen.
 *
 * A FAILURE sets `degraded: true`; a query that legitimately matched nothing
 * returns `degraded: false`. The distinction is the whole point — a renderer
 * has to say "search is having trouble, try again" in the first case and
 * "no results for X" in the second, and it cannot tell them apart from an
 * empty `results` array alone. This mirrors the server, which sets the same
 * flag when its own fan-out fails. The short-query guard below is not a
 * failure, so it stays false.
 */
export async function fetchSearch(
  query: string,
  opts: FetchSearchOptions = {},
): Promise<SiteSearchResponse> {
  if (!query || query.trim().length < MIN_QUERY_LENGTH) {
    return EMPTY_SEARCH_RESPONSE;
  }

  const params = new URLSearchParams({
    q: query.trim(),
    mode: opts.mode ?? 'suggest',
    country: getCountry(),
  });
  if (opts.types?.length) params.set('types', opts.types.join(','));
  if (opts.page) params.set('page', String(opts.page));
  if (opts.limit) params.set('limit', String(opts.limit));

  // Analytics tags. Always sent, never conditional: a search the dashboard
  // cannot attribute to a surface and a brand is a search it has to drop.
  params.set('surface', opts.surface ?? 'app-header');
  params.set('brand', BRAND.key);
  params.set('sid', getSearchSessionId());

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  try {
    const response = await fetch(`${API_BASE}/search?${params.toString()}`, {
      headers: { 'Content-Type': 'application/json' },
      mode: 'cors',
      credentials: 'omit',
      signal: controller.signal,
    });
    if (!response.ok) return { ...EMPTY_SEARCH_RESPONSE, degraded: true };
    return (await response.json()) as SiteSearchResponse;
  } catch {
    // Covers network failure, malformed JSON (response.json() throwing) and
    // the AbortController firing on a hung request.
    return { ...EMPTY_SEARCH_RESPONSE, degraded: true };
  } finally {
    // Must run on every exit path — a missing clearTimeout here is a known
    // leak in this codebase's history (see fetchUnifiedSearchResults).
    clearTimeout(timeout);
  }
}
